import { useState, useMemo } from "react";
import { BadgeCheck, ChevronDown, ChevronUp, Info, Loader2, Search, Sparkles, Wrench } from "lucide-react";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { SeverityBadge, type IssueSeverity } from "./SeverityBadge";
import { StatusBadge } from "./StatusBadge";
import {
	IssueTableFilters,
	type IssueFilters,
	type IssueType,
} from "./IssueTableFilters";
import { getIssueTypeHelp } from "./issueTypeHelp";
import { RecoveryActions } from "./RecoveryActions";
import { getReadabilityDisplayStatus } from "./readabilityWorkflow";
import { isReviewOnlyIssue } from "./reviewOnly";
import {
	getIssueTypeLabel,
	getLargeImageResultState,
	getCanonicalIssueSubtype,
	type GenerationStatus,
	type IssueStatus,
	type LargeImageProofResult,
	type LargeImageResultState,
} from "@/types/technicalAudit";

interface IssueDetails {
	current_title?: string | null;
	current_meta_description?: string | null;
	current_h1s?: string[];
	image_url?: string;
	redirect_chain?: string[];
	[key: string]: unknown;
}

interface ProposedFix {
	new_seo_title?: string;
	new_title?: string;
	target_field?: string;
	new_meta_description?: string;
	new_h1?: string;
	new_alt_text?: string;
	recommended_target?: string;
	[key: string]: unknown;
}

export interface PageIssue {
	id: string;
	run_id: string;
	issue_type: IssueType;
	source?: "screaming_frog" | "dataforseo" | string;
	page_url: string;
	page_title: string | null;
	status_code: number | null;
	indexability: string | null;
	severity: IssueSeverity;
	content_type?: "page" | "post" | "product" | "media" | null;
	wordpress_id?: number | null;
	modified_date?: string | null;
	fix_target?: "blog" | "page" | null;
	auto_fix_excluded_reason?: string | null;
	// Background auto-suggestion generation state (MVP). Distinct from
	// fix_status so a generation failure never collides with the apply Verify
	// flow. Null when the row was never auto-queued.
	generation_status?: GenerationStatus | null;
	issue_details: IssueDetails;
	fix_status: IssueStatus;
	proposed_fix: ProposedFix | null;
	proposed_value?: unknown;
	approved_value: ProposedFix | null;
	error: string | null;
	created_at: string;
	approved_at?: string | null;
	applied_at?: string | null;
	large_image_result?: LargeImageProofResult | null;
	apply_result?: LargeImageProofResult | Record<string, unknown> | null;
	fix_result?: LargeImageProofResult | Record<string, unknown> | null;
	result?: LargeImageProofResult | Record<string, unknown> | null;
}

const SEO_META_EDIT_TYPES = new Set<IssueType>([
	"title_meta",
	"duplicate_title",
	"page_title_same_as_h1",
	"page_title_missing",
	"page_title_over_60_chars",
	"page_title_below_30_chars",
	"duplicate_meta",
	"meta_missing",
	"meta_description_over_155_chars",
	"meta_description_below_70_chars",
	"title_too_short",
	"serp_title_truncated",
	"serp_meta_truncated",
]);

interface IssueTableProps {
	issues: PageIssue[];
	selectedIds: string[];
	onSelectionChange: (ids: string[]) => void;
	onIssueClick: (issue: PageIssue) => void;
	onApplyIssue?: (issueId: string) => void;
	onVerifyIssue?: (issueId: string) => void;
	/**
	 * Clear a failed row's red badge by resetting fix_status back to "pending"
	 * (PATCH {fixStatus:"pending"}). Lets the user escape the "failed" dead-end
	 * without re-applying.
	 */
	onDismissError?: (issueId: string) => void;
	filters?: IssueFilters;
	onFiltersChange?: (filters: IssueFilters) => void;
	isLoading?: boolean;
	/** A filter/search refetch is in flight (rows already on screen). */
	isFetching?: boolean;
}

type SortField = "page_url" | "issue_type" | "severity" | "status" | "created_at" | "last_action";
type SortDirection = "asc" | "desc";

interface SortConfig {
	field: SortField;
	direction: SortDirection;
}

function renderSortIcon(sort: SortConfig, field: SortField) {
	if (sort.field !== field) return null;
	return sort.direction === "asc" ? (
		<ChevronUp className="inline h-4 w-4 ml-1" />
	) : (
		<ChevronDown className="inline h-4 w-4 ml-1" />
	);
}

function calculateSeverity(issue: PageIssue): IssueSeverity {
	if (issue.severity === "critical" || issue.severity === "warning" || issue.severity === "info") {
		return issue.severity;
	}

	switch (issue.issue_type) {
		case "title_meta": {
			const details = issue.issue_details;
			if (details.title_missing || details.meta_missing) return "critical";
			if (details.title_too_long || details.meta_too_long) return "warning";
			return "info";
		}
		case "h1": {
			const details = issue.issue_details;
			if (details.h1_missing) return "critical";
			if (details.multiple_h1s) return "warning";
			return "info";
		}
		case "h1_missing":
			return "critical";
		case "h1_multiple":
			return "warning";
		case "h1_too_long":
			return "info";
		case "alt_text":
			return "warning";
		case "redirect": {
			const details = issue.issue_details;
			const redirectCount = details.redirect_count as number;
			if (redirectCount > 3) return "critical";
			if (redirectCount > 1) return "warning";
			return "info";
		}
		case "broken_link": {
			const details = issue.issue_details;
			const statusCode = details.status_code as number;
			if ([404, 410, 500, 502, 503].includes(statusCode)) return "critical";
			return "warning";
		}
		case "broken_internal":
			return "critical";
		case "canonical": {
			const issueSubtype = getCanonicalIssueSubtype(issue.issue_details);
			if (issueSubtype === "missing") return "critical";
			return "warning";
		}
		case "duplicate_title":
			return "warning";
		case "page_title_missing":
		case "meta_missing":
			return "critical";
		case "page_title_over_60_chars":
		case "meta_description_over_155_chars":
			return "warning";
		case "page_title_same_as_h1":
		case "page_title_below_30_chars":
		case "meta_description_below_70_chars":
			return "info";
		case "duplicate_meta":
			return "warning";
		case "thin_content": {
			const wordCount = issue.issue_details.word_count as number;
			if (wordCount < 100) return "critical";
			return "warning";
		}
		case "indexability":
			return "critical";
		case "slow_response": {
			const responseTime = issue.issue_details.response_time_ms as number;
			if (responseTime > 5000) return "critical";
			return "warning";
		}
		case "near_duplicate": {
			const score = issue.issue_details.similarity_score as number;
			if (score > 0.95) return "critical";
			return "warning";
		}
		case "orphan_page":
			return "warning";
		case "schema_error":
			return "warning";
		case "missing_schema":
			return "warning";
		case "schema_validation_error":
			return ((issue.issue_details?.error_count as number) ?? 0) >= 3
				? "critical"
				: "warning";
		case "missing_og_tags":
			return "warning";
		case "mixed_content":
			return "warning";
		case "redirect_loop":
			return "critical";
		case "title_too_short":
			return "warning";
		case "serp_title_truncated":
			return "warning";
		case "serp_meta_truncated":
			return "info";
		case "multiple_meta":
			return "warning";
		case "duplicate_h1":
			return "warning";
		case "low_readability":
			return "info";
		case "low_text_ratio":
			return "info";
		case "stale_content":
			return "info";
		case "broken_external": {
			const statusCode2 = issue.issue_details.status_code as number;
			if ([404, 410, 500, 502, 503].includes(statusCode2)) return "critical";
			return "warning";
		}
		case "links_to_broken":
			// Source-page-anchored (lists N broken internal targets); high severity
			// like broken_internal, independent of individual status codes.
			return "critical";
		case "deep_page": {
			const depth = issue.issue_details.click_depth as number;
			if (depth > 5) return "critical";
			return "warning";
		}
		case "large_image": {
			const imgSize = issue.issue_details.file_size_bytes as number;
			if (imgSize > 2_000_000) return "critical";
			return "warning";
		}
		case "broken_image":
			return "critical";
		case "non_nextgen_image":
			return "info";
		case "large_page": {
			const pageSize2 = issue.issue_details.page_size_bytes as number;
			if (pageSize2 > 5_000_000) return "critical";
			return "warning";
		}
		case "unsafe_crossorigin":
			return "info";
		case "nofollow_internal":
			return "warning";
		case "low_internal_links":
			return "warning";
		case "missing_hsts":
			return "info";
		case "hreflang_error":
			return "warning";
		case "sitemap_error":
			return "warning";
		case "missing_from_sitemap":
			return "warning";
		case "performance_diagnostics":
			return "warning";
		case "keyword_cannibalization":
			return "warning";
		case "image_missing_dimensions":
			return "info";
		case "core_web_vitals":
			return "warning";
		default:
			return "info";
	}
}

function truthyDetailFlag(details: IssueDetails, key: string): boolean {
	const value = details[key];
	if (typeof value === "boolean") return value;
	if (typeof value === "string") {
		return ["true", "1", "yes"].includes(value.trim().toLowerCase());
	}
	return Boolean(value);
}

function detailNumber(details: IssueDetails, key: string): number | null {
	const value = details[key];
	if (typeof value === "number" && Number.isFinite(value)) return value;
	if (typeof value === "string") {
		const parsed = Number.parseInt(value.trim(), 10);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
}

function matchesIssueTypeFilter(issue: PageIssue, type: IssueType): boolean {
	if (issue.issue_type === type) return true;
	if (issue.issue_type !== "h1") return false;

	const details = issue.issue_details;
	const h1Count = detailNumber(details, "h1_count");
	if (type === "h1_missing") {
		return truthyDetailFlag(details, "h1_missing") || h1Count === 0;
	}
	if (type === "h1_multiple") {
		return (
			truthyDetailFlag(details, "multiple_h1s") ||
			(h1Count !== null && h1Count > 1)
		);
	}
	if (type === "h1_too_long") {
		return truthyDetailFlag(details, "h1_too_long");
	}
	return false;
}

function getIssueTimestampValue(value?: string | null) {
	if (!value) return 0;
	const timestamp = new Date(value).getTime();
	return Number.isNaN(timestamp) ? 0 : timestamp;
}

function getLastActionTimestamp(issue: PageIssue) {
	return Math.max(
		getIssueTimestampValue(issue.applied_at),
		getIssueTimestampValue(issue.approved_at),
		getIssueTimestampValue(issue.modified_date),
	);
}

function formatIssueDate(value?: string | null) {
	if (!value) return "Not available";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "Not available";
	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "2-digit",
	});
}

function hasSuggestedFix(issue: PageIssue) {
	return Boolean(issue.approved_value || issue.proposed_fix || issue.proposed_value);
}

// A background auto-suggestion job is in flight (queued or generating) and has
// not yet produced a suggestion. Drives the "Generating" label + spinner and
// disables the row action so a manual click can't double-spend the provider.
export function isAutoSuggestGenerating(issue: PageIssue) {
	if (hasSuggestedFix(issue)) return false;
	return issue.generation_status === "queued" || issue.generation_status === "generating";
}

// Background generation failed and produced nothing — offer an explicit retry.
export function isAutoSuggestFailed(issue: PageIssue) {
	return !hasSuggestedFix(issue) && issue.generation_status === "failed";
}

function isInternalLinkIssue(issue: PageIssue) {
	return issue.issue_type === "orphan_page" || issue.issue_type === "low_internal_links";
}

function getLargeImageActionLabel(
	issue: PageIssue,
	resultState: LargeImageResultState | null,
) {
	if (resultState === "applied" || (!resultState && issue.fix_status === "applied")) {
		return "Applied";
	}
	if (resultState === "skipped") return "Skipped";
	if (resultState === "protected") return "Protected";
	if (resultState === "failed" || issue.fix_status === "failed") {
		return "Retry optimize";
	}
	return "Optimize image";
}

export function getIssueActionLabel(
	issue: PageIssue,
	fixMode: string,
	resultState: LargeImageResultState | null = null,
) {
	if (issue.issue_type === "large_image") {
		return getLargeImageActionLabel(issue, resultState);
	}
	// Background auto-suggestion state takes precedence (no suggestion yet):
	// "Generating" while in flight, "Retry generation" after a failure. Once a
	// suggestion lands these are skipped and the normal review labels apply.
	if (isAutoSuggestGenerating(issue)) {
		return "Generating";
	}
	if (isAutoSuggestFailed(issue)) {
		return "Retry generation";
	}
	if (issue.issue_type === "low_readability") {
		return hasSuggestedFix(issue) ? "Review edits" : "Open generator";
	}
	if (issue.issue_type === "thin_content") {
		return hasSuggestedFix(issue) ? "Review expansion" : "Generate expansion";
	}
	if (isInternalLinkIssue(issue)) {
		return hasSuggestedFix(issue) ? "Review fix" : "Open generator";
	}
	if (issue.fix_status === "approved") {
		return fixMode === "auto" ? "Review fix" : "Review";
	}
	if (fixMode === "ai" && !hasSuggestedFix(issue)) {
		return "Generate suggestion";
	}
	return fixMode === "auto" ? "Review fix" : "Review";
}

function shouldOpenIssueDrawer(issue: PageIssue) {
	if (issue.issue_type === "large_image") return false;
	return true;
}

function isLargeImageResultTerminal(
	issue: PageIssue,
	resultState: LargeImageResultState | null,
) {
	return (
		resultState === "applied" ||
		resultState === "skipped" ||
		resultState === "protected" ||
		(!resultState && issue.fix_status === "applied")
	);
}

export function IssueTable({
	issues,
	selectedIds,
	onSelectionChange,
	onIssueClick,
	onApplyIssue,
	onVerifyIssue,
	onDismissError,
	filters: controlledFilters,
	onFiltersChange: controlledOnFiltersChange,
	isLoading = false,
	isFetching = false,
}: IssueTableProps) {
	const [sort, setSort] = useState<SortConfig>({
		field: "severity",
		direction: "desc",
	});
	const [localFilters, setLocalFilters] = useState<IssueFilters>({});
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 20;
	const filters = controlledFilters ?? localFilters;
	const usesServerFilters = Boolean(controlledOnFiltersChange);

	const filteredAndSortedIssues = useMemo(() => {
		let result = [...issues];

		if (!usesServerFilters) {
			if (filters.type) {
				result = result.filter((issue) =>
					matchesIssueTypeFilter(issue, filters.type as IssueType),
				);
			}
			if (filters.status) {
				result = result.filter((issue) => issue.fix_status === filters.status);
			}
			if (filters.severity) {
				result = result.filter(
					(issue) => calculateSeverity(issue) === filters.severity,
				);
			}
			if (filters.content_type) {
				result = result.filter(
					(issue) => issue.content_type === filters.content_type,
				);
			}
			if (filters.search) {
				const searchLower = filters.search.toLowerCase();
				result = result.filter((issue) =>
					issue.page_url.toLowerCase().includes(searchLower),
				);
			}
		}

		// Apply sorting
		result.sort((a, b) => {
			let aValue: string | number;
			let bValue: string | number;

			switch (sort.field) {
				case "page_url":
					aValue = a.page_url;
					bValue = b.page_url;
					break;
				case "issue_type":
					aValue = a.issue_type;
					bValue = b.issue_type;
					break;
				case "severity": {
					const severityOrder = { critical: 3, warning: 2, info: 1 };
					aValue = severityOrder[calculateSeverity(a)];
					bValue = severityOrder[calculateSeverity(b)];
					break;
				}
				case "status":
					aValue = a.fix_status;
					bValue = b.fix_status;
					break;
				case "created_at":
					aValue = getIssueTimestampValue(a.created_at);
					bValue = getIssueTimestampValue(b.created_at);
					break;
				case "last_action":
					aValue = getLastActionTimestamp(a);
					bValue = getLastActionTimestamp(b);
					break;
				default:
					return 0;
			}

			if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
			if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
			return 0;
		});

		return result;
	}, [issues, filters, sort, usesServerFilters]);

	const paginatedIssues = useMemo(() => {
		if (usesServerFilters) return filteredAndSortedIssues;
		const startIndex = (currentPage - 1) * pageSize;
		return filteredAndSortedIssues.slice(startIndex, startIndex + pageSize);
	}, [filteredAndSortedIssues, currentPage, usesServerFilters]);

	const totalPages = usesServerFilters
		? 1
		: Math.ceil(filteredAndSortedIssues.length / pageSize);

	const handleSort = (field: SortField) => {
		setSort((prev) => ({
			field,
			direction:
				prev.field === field && prev.direction === "asc" ? "desc" : "asc",
		}));
	};

	const handleFiltersChange = (nextFilters: IssueFilters) => {
		if (controlledOnFiltersChange) {
			controlledOnFiltersChange(nextFilters);
		} else {
			setLocalFilters(nextFilters);
		}
		setCurrentPage(1);
	};

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			onSelectionChange(paginatedIssues.map((issue) => issue.id));
		} else {
			onSelectionChange([]);
		}
	};

	const handleSelectOne = (issueId: string, checked: boolean) => {
		if (checked) {
			onSelectionChange([...selectedIds, issueId]);
		} else {
			onSelectionChange(selectedIds.filter((id) => id !== issueId));
		}
	};

	const isAllSelected =
		paginatedIssues.length > 0 &&
		paginatedIssues.every((issue) => selectedIds.includes(issue.id));

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="text-muted-foreground">Loading issues...</div>
			</div>
		);
	}

	return (
		<TooltipProvider>
		<div className="space-y-4">
			{!usesServerFilters && (
				<IssueTableFilters
					filters={filters}
					onFiltersChange={handleFiltersChange}
				/>
			)}

			<div className="relative rounded-md border">
				{isFetching && (
					<div
						className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 rounded-md bg-warm-bg/70 backdrop-blur-[1px]"
						role="status"
						aria-live="polite"
					>
						<Search className="h-6 w-6 text-coral animate-search-sweep" />
						<span className="text-xs font-medium text-coral-dark">Filtering issues…</span>
					</div>
				)}
				<Table containerClassName="max-h-[calc(100vh-16rem)]">
					<TableHeader className="sticky top-0 z-10 [&_th]:bg-muted">
						<TableRow>
							<TableHead className="w-12">
								<Checkbox
									checked={isAllSelected}
									onCheckedChange={handleSelectAll}
									aria-label="Select all"
								/>
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("severity")}
							>
								Severity
								{renderSortIcon(sort, "severity")}
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("issue_type")}
							>
								Type
								{renderSortIcon(sort, "issue_type")}
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("page_url")}
							>
								Page URL
								{renderSortIcon(sort, "page_url")}
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("created_at")}
							>
								Discovered
								{renderSortIcon(sort, "created_at")}
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("last_action")}
							>
								Last Action
								{renderSortIcon(sort, "last_action")}
							</TableHead>
							<TableHead
								className="cursor-pointer hover:bg-muted/50"
								onClick={() => handleSort("status")}
							>
								Status
								{renderSortIcon(sort, "status")}
							</TableHead>
							<TableHead className="w-56 text-right">Action</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{paginatedIssues.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={8}
									className="text-center py-12 text-muted-foreground"
								>
									No issues found
								</TableCell>
							</TableRow>
						) : (
							paginatedIssues.map((issue) => {
								const isSelected = selectedIds.includes(issue.id);
								const severity = calculateSeverity(issue);
								const typeHelp = getIssueTypeHelp(issue.issue_type);
								const isLargeImage = issue.issue_type === "large_image";
								const largeImageResultState = isLargeImage
									? getLargeImageResultState(issue)
									: null;
								const actionLabel = getIssueActionLabel(
									issue,
									typeHelp.fixMode,
									largeImageResultState,
								);
									const actionOpensDrawer = shouldOpenIssueDrawer(issue);
								const largeImageTerminal = isLargeImageResultTerminal(
									issue,
									largeImageResultState,
								);
								const opensGenerator =
									!hasSuggestedFix(issue) &&
									(typeHelp.fixMode === "ai" ||
										issue.issue_type === "low_readability" ||
										isInternalLinkIssue(issue));
								const isGenerating = isAutoSuggestGenerating(issue);
								const ActionIcon = isGenerating
									? Loader2
									: opensGenerator
										? Sparkles
										: Wrench;
								// Page-body fixes can still be excluded, but SEO title/meta
								// fixes write only Yoast/RankMath meta keys and are safe for
								// confirmed pages as well as posts.
								const isSeoMetaEditIssue = SEO_META_EDIT_TYPES.has(
									issue.issue_type,
								);
								const isPageExcluded =
									issue.fix_target === "page" && !isSeoMetaEditIssue;
								const isExcluded = isPageExcluded;
								const statusBadge = (
									<StatusBadge
										status={getReadabilityDisplayStatus(issue)}
										resultState={largeImageResultState}
										reviewOnly={isReviewOnlyIssue(issue)}
									/>
								);

								return (
									<TableRow
										key={issue.id}
										data-state={isSelected ? "selected" : undefined}
										className="cursor-pointer"
										onClick={(e) => {
											if (
												(e.target as HTMLElement).closest('[role="checkbox"]')
											) {
												return;
											}
											onIssueClick(issue);
										}}
									>
										<TableCell onClick={(e) => e.stopPropagation()}>
											<Checkbox
												checked={isSelected}
												onCheckedChange={(checked) =>
													handleSelectOne(issue.id, checked as boolean)
												}
												aria-label={`Select ${issue.page_url}`}
											/>
										</TableCell>
										<TableCell>
											<SeverityBadge severity={severity} />
										</TableCell>
										<TableCell>
											<div className="flex flex-col gap-1">
												<div className="flex items-center gap-1.5">
													<span>{getIssueTypeLabel(issue)}</span>
													<Tooltip>
														<TooltipTrigger asChild>
															<span
																className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
																onClick={(e) => e.stopPropagation()}
															>
																<Info className="h-3.5 w-3.5" />
															</span>
														</TooltipTrigger>
														<TooltipContent className="max-w-xs p-3">
															<div className="space-y-2">
																<div className="font-medium">{typeHelp.title}</div>
																<div>{typeHelp.description}</div>
																<div className="text-xs text-muted-foreground">
																	{typeHelp.typicalFix}
																</div>
															</div>
														</TooltipContent>
													</Tooltip>
												</div>
											</div>
										</TableCell>
										<TableCell
											className="max-w-md truncate"
											title={issue.page_url}
										>
											{issue.page_url}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{formatIssueDate(issue.created_at)}
										</TableCell>
										<TableCell className="text-sm text-muted-foreground">
											{getLastActionTimestamp(issue) > 0
												? formatIssueDate(
														issue.applied_at || issue.approved_at || issue.modified_date,
													)
												: "Not available"}
										</TableCell>
										<TableCell>
											{(issue.fix_status === "failed" ||
												issue.fix_status === "obsolete") &&
											issue.error ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="inline-flex cursor-help">
															{statusBadge}
														</span>
													</TooltipTrigger>
													<TooltipContent className="max-w-sm p-3 text-sm">
														{issue.error}
													</TooltipContent>
												</Tooltip>
											) : (
												statusBadge
											)}
										</TableCell>
										<TableCell
											className="text-right"
											onClick={(e) => e.stopPropagation()}
										>
											{!isLargeImage &&
											!isInternalLinkIssue(issue) &&
											issue.fix_status === "failed" ? (
												// A failed apply must not dead-end on "Verify" (which
												// neither re-applies nor clears the red badge). Offer
												// Retry (re-run the same apply path) + Dismiss (reset
												// fix_status → pending, clearing the badge).
												<div className="flex flex-wrap items-center justify-end gap-1.5">
													<RecoveryActions
														issueId={issue.id}
														isFailed
														compact
														className="justify-end"
													/>
													<Button
														variant="outline"
														size="sm"
														onClick={() => {
															if (actionOpensDrawer) {
																onIssueClick(issue);
															} else {
																onApplyIssue?.(issue.id);
															}
														}}
														disabled={!onApplyIssue && !actionOpensDrawer}
													>
														<Wrench className="h-3.5 w-3.5 mr-1.5" />
														Retry
													</Button>
													<Button
														variant="ghost"
														size="sm"
														className="text-destructive hover:text-destructive hover:bg-destructive/10"
														onClick={() => onDismissError?.(issue.id)}
														disabled={!onDismissError}
													>
														Dismiss
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => onVerifyIssue?.(issue.id)}
														disabled={!onVerifyIssue}
													>
														<BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
														Verify
													</Button>
												</div>
											) : !isLargeImage &&
											!isInternalLinkIssue(issue) &&
											issue.fix_status === "applied" ? (
												<Button
													variant="outline"
													size="sm"
													onClick={() => onVerifyIssue?.(issue.id)}
													disabled={!onVerifyIssue}
												>
													<BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
													Verify
												</Button>
											) : issue.fix_status === "verified" ? (
												<Button variant="outline" size="sm" disabled>
													<BadgeCheck className="h-3.5 w-3.5 mr-1.5" />
													Verified
												</Button>
											) : issue.fix_status === "recommended" ? (
												// Recommendation-only: nothing was written to
												// WordPress, so there is nothing to Verify. Show a
												// calm "apply manually" cue instead of a Verify button.
												<span className="inline-flex items-center rounded-full border border-coral/40 bg-coral-light px-2 py-0.5 text-xs font-medium text-coral-dark">
													Apply manually
												</span>
											) : isPageExcluded ? (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
															Page — excluded
														</span>
													</TooltipTrigger>
													<TooltipContent className="max-w-xs p-3">
														Only blog posts are auto-fixed; pages are excluded.
													</TooltipContent>
												</Tooltip>
											) : (
												<Button
													variant="outline"
													size="sm"
													onClick={() => {
														if (actionOpensDrawer) {
															onIssueClick(issue);
														} else {
															onApplyIssue?.(issue.id);
														}
													}}
														disabled={
															!onApplyIssue ||
															issue.fix_status === "rejected" ||
															issue.fix_status === "skipped" ||
															issue.fix_status === "obsolete" ||
															isExcluded ||
														isGenerating ||
														(isLargeImage && largeImageTerminal)
													}
												>
														<ActionIcon
															className={`h-3.5 w-3.5 mr-1.5${isGenerating ? " animate-spin" : ""}`}
														/>
														{actionLabel}
													</Button>
											)}
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</div>

			{!usesServerFilters && totalPages > 1 && (
				<div className="flex items-center justify-center gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
						disabled={currentPage === 1}
					>
						Previous
					</Button>
					<span className="text-sm text-muted-foreground">
						Page {currentPage} of {totalPages}
					</span>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
						disabled={currentPage === totalPages}
					>
						Next
					</Button>
				</div>
			)}
		</div>
		</TooltipProvider>
	);
}
